<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { CalendarClock, FolderOpen, TrendingUp, Users } from '@lucide/vue';
import { http } from '@/api/client';

definePage({
  meta: { titolo: 'Dashboard' },
});

/**
 * ANTEPRIMA, non dati reali: la Dashboard operatori (KPI "progetti
 * aperti/assegnati" derivato dalle fasi) non è ancora costruita —
 * i KPI da mostrare vanno decisi con Evolfin. Questa pagina mostra solo la
 * FORMA che avrà (KPI, andamento, lista per operatore), in tinte
 * volutamente spente, così nessuno la scambia per un numero vero. Va
 * sostituita con l'aggregato reale una volta chiusa quella decisione — non
 * aggiungere query o calcoli qui finché resta un mockup.
 */
const { data, isPending, isError } = useQuery({
  queryKey: ['health'],
  queryFn: async () => {
    const { data } = await http.get<{ status: string; database: boolean; valkey: boolean }>(
      '/readyz',
    );
    return data;
  },
});

const kpi = [
  { etichetta: 'Progetti aperti', icona: FolderOpen },
  { etichetta: 'Scadenze imminenti', icona: CalendarClock },
  { etichetta: 'Valore pratica', icona: TrendingUp },
  { etichetta: 'Operatori al lavoro', icona: Users },
];

// Altezze fisse (non casuali): un mockup deve restare identico a ogni render.
const barre = [38, 62, 45, 70, 52, 80, 58];
const righeLista = [72, 45, 88, 30, 60];
</script>

<template>
  <div class="flex flex-col gap-6">
    <header>
      <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Panoramica</p>
      <h1 class="mt-1 font-display text-[33px] leading-[1.07] font-semibold">Dashboard</h1>
    </header>

    <p
      class="rounded-md border border-line bg-[var(--surface-accent-soft)] px-4 py-3 text-[13px] text-ink-secondary"
    >
      <b>Anteprima: questa dashboard è un mockup, non dati reali. I KPI da definire</b>
    </p>

    <section aria-hidden="true" class="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <div v-for="k in kpi" :key="k.etichetta" class="card flex flex-col gap-3 p-5">
        <div class="flex items-center justify-between">
          <span class="text-[12px] font-medium text-ink-muted">{{ k.etichetta }}</span>
          <span
            class="grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--color-bg-soft)] text-ink-muted"
          >
            <component :is="k.icona" :size="15" :stroke-width="1.8" />
          </span>
        </div>
        <div class="h-7 w-16 rounded-md bg-[var(--color-bg-soft)]" />
        <div class="h-2.5 w-20 rounded-sm bg-[var(--color-bg-soft)]" />
      </div>
    </section>

    <div class="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
      <section aria-hidden="true" class="card flex flex-col gap-4 p-5">
        <div class="flex items-center justify-between">
          <div class="h-3.5 w-40 rounded-sm bg-[var(--color-bg-soft)]" />
          <div class="h-3.5 w-16 rounded-sm bg-[var(--color-bg-soft)]" />
        </div>
        <div class="flex h-40 items-end gap-3 border-b border-line-soft pb-0.5">
          <div
            v-for="(h, i) in barre"
            :key="i"
            class="flex-1 rounded-t-sm bg-[var(--color-bg-soft)]"
            :style="{ height: `${h}%` }"
          />
        </div>
      </section>

      <section aria-hidden="true" class="card flex flex-col gap-4 p-5">
        <div class="h-3.5 w-36 rounded-sm bg-[var(--color-bg-soft)]" />
        <div class="flex flex-col gap-3.5">
          <div v-for="(l, i) in righeLista" :key="i" class="flex items-center gap-3">
            <div class="size-8 shrink-0 rounded-full bg-[var(--color-bg-soft)]" />
            <div class="flex-1">
              <div class="h-2.5 rounded-sm bg-[var(--color-bg-soft)]" :style="{ width: `${l}%` }" />
            </div>
          </div>
        </div>
      </section>
    </div>

    <div class="card max-w-md p-6">
      <h2 class="text-[15px] font-semibold">Stato del sistema</h2>
      <div class="mt-4 border-t border-line-soft pt-4 text-ink-secondary">
        <p v-if="isPending">Verifica in corso…</p>
        <p v-else-if="isError">API non raggiungibile.</p>
        <dl v-else class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <dt>Database</dt>
            <dd>{{ data?.database ? '● Connesso' : '○ Non raggiungibile' }}</dd>
          </div>
          <div class="flex items-center justify-between">
            <dt>Keystore</dt>
            <dd>{{ data?.valkey ? '● Connesso' : '○ Non raggiungibile' }}</dd>
          </div>
        </dl>
      </div>
    </div>
  </div>
</template>
